import { ENDPOINTS } from '@/constants/api'
import { QUERY_KEYS } from '@/constants/query-keys'
import type { QueryOf, RequestBodyOf, ResponseData } from '@/types/api'
import { apiSlice } from './base'

export type AlarmEventsData = ResponseData<'/api/v1/alarms/events', 'get'>
export type AlarmEvent = AlarmEventsData['items'][number]
export type AlarmRule = ResponseData<'/api/v1/alarms/rules/{id}', 'get'>
type AlarmEventsQuery = QueryOf<'/api/v1/alarms/events', 'get'>
type AlarmRulesData = ResponseData<'/api/v1/alarms/rules', 'get'>
type AlarmRulesQuery = QueryOf<'/api/v1/alarms/rules', 'get'>
type CreateRuleBody = RequestBodyOf<'/api/v1/alarms/rules', 'post'>
type UpdateRuleBody = RequestBodyOf<'/api/v1/alarms/rules/{id}', 'patch'>

export const alarmsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAlarmEvents: builder.query<AlarmEventsData, AlarmEventsQuery | void>({
      query: (params) => ({ url: ENDPOINTS.ALARMS.EVENTS, ...(params ? { params } : {}) }),
      transformResponse: (response: { data: AlarmEventsData }) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.items.map((a) => ({ type: QUERY_KEYS.ALARM, id: a.id }) as const),
              { type: QUERY_KEYS.ALARM, id: 'LIST' } as const,
            ]
          : [{ type: QUERY_KEYS.ALARM, id: 'LIST' } as const],
    }),
    acknowledgeAlarm: builder.mutation<AlarmEvent, string>({
      query: (id) => ({ url: ENDPOINTS.ALARMS.ACKNOWLEDGE(id), method: 'POST' }),
      transformResponse: (response: { data: AlarmEvent }) => response.data,
      invalidatesTags: (_r, _e, id) => [
        { type: QUERY_KEYS.ALARM, id },
        { type: QUERY_KEYS.ALARM, id: 'LIST' },
      ],
    }),
    getAlarmRules: builder.query<AlarmRulesData, AlarmRulesQuery | void>({
      query: (params) => ({ url: ENDPOINTS.ALARMS.RULES, ...(params ? { params } : {}) }),
      transformResponse: (response: { data: AlarmRulesData }) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.items.map((r) => ({ type: QUERY_KEYS.ALARM_RULE, id: r.id }) as const),
              { type: QUERY_KEYS.ALARM_RULE, id: 'LIST' } as const,
            ]
          : [{ type: QUERY_KEYS.ALARM_RULE, id: 'LIST' } as const],
    }),
    getAlarmRuleById: builder.query<AlarmRule, string>({
      query: (id) => ENDPOINTS.ALARMS.RULE_BY_ID(id),
      transformResponse: (response: { data: AlarmRule }) => response.data,
      providesTags: (_r, _e, id) => [{ type: QUERY_KEYS.ALARM_RULE, id }],
    }),
    createAlarmRule: builder.mutation<AlarmRule, CreateRuleBody>({
      query: (body) => ({ url: ENDPOINTS.ALARMS.RULES, method: 'POST', body }),
      transformResponse: (response: { data: AlarmRule }) => response.data,
      invalidatesTags: [{ type: QUERY_KEYS.ALARM_RULE, id: 'LIST' }],
    }),
    updateAlarmRule: builder.mutation<AlarmRule, { id: string; body: UpdateRuleBody }>({
      query: ({ id, body }) => ({ url: ENDPOINTS.ALARMS.RULE_BY_ID(id), method: 'PATCH', body }),
      transformResponse: (response: { data: AlarmRule }) => response.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: QUERY_KEYS.ALARM_RULE, id },
        { type: QUERY_KEYS.ALARM_RULE, id: 'LIST' },
      ],
    }),
    deleteAlarmRule: builder.mutation<void, string>({
      query: (id) => ({ url: ENDPOINTS.ALARMS.RULE_BY_ID(id), method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: QUERY_KEYS.ALARM_RULE, id },
        { type: QUERY_KEYS.ALARM_RULE, id: 'LIST' },
      ],
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetAlarmEventsQuery,
  useAcknowledgeAlarmMutation,
  useGetAlarmRulesQuery,
  useGetAlarmRuleByIdQuery,
  useCreateAlarmRuleMutation,
  useUpdateAlarmRuleMutation,
  useDeleteAlarmRuleMutation,
} = alarmsApi
